import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosDefault, { AxiosError } from "axios";
import axiosGetToken from "../auth/axiosGetToken";
import { setUserIp } from "../auth/authSlice";

interface PaymentIntentResponse {
  clientSecret: string;
  paymentId: string;
  paymentSession: Record<string, unknown>;
  availablePayments: any[];
  countryCode?: string;
  currency?: string;
}

export interface ConsentPayload {
  type: string;
  version: string;
  acceptedAt: string;
  userAgent?: string;
}

interface PaymentIntentRequest {
  amount: number;
  items: {
    productId: string;
    platformGroupId: string;
    price: number;
    quantity: number;
  }[];
  consents?: ConsentPayload[];
}

interface PaymentIntentArgs extends PaymentIntentRequest {
  provider?: string;
}

interface PaymentProviderResponse {
  activeProvider: string;
  availableProviders: string[];
  backupOrder: string[];
}
// Thunk for fetching order status

export const createPaymentIntent = createAsyncThunk<
  PaymentIntentResponse,
  PaymentIntentArgs,
  { rejectValue: string }
>("payment/create-payment-intent", async (data, { dispatch, rejectWithValue }) => {
  try {
    const { provider, ...payload } = data;
    // Ensure idempotent retries from FE: stable key per session
    const keyPrefix = (provider || "checkout").toLowerCase();
    const storageKey = `${keyPrefix}_idempotency_key`;
    let idem = window.sessionStorage.getItem(storageKey);
    if (!idem) {
      const cryptoObj = typeof window !== "undefined" ? window.crypto : undefined;
      const generatedId =
        cryptoObj && "randomUUID" in cryptoObj
          ? (cryptoObj.randomUUID as () => string)()
          : String(Date.now());
      window.sessionStorage.setItem(storageKey, generatedId);
      idem = generatedId;
    }
    const idempotencyKey = idem as string;
    const response = await axiosGetToken.put(
      "/v1/payment/create-payment-intent",
      provider ? { ...payload, provider } : payload,
      {
        headers: {
          'X-Idempotency-Key': idempotencyKey,
          ...(keyPrefix === 'checkout' ? { 'Cko-Idempotency-Key': idempotencyKey } : {}),
        },
      },
    );
    if (response.status === 200) {
      const headers = response.headers ?? {};
      const regionHeader =
        (headers["x-region"] as string | undefined) ??
        (headers["X-Region"] as string | undefined);
      const currencyHeader =
        (headers["x-currency"] as string | undefined) ??
        (headers["X-Currency"] as string | undefined);
      const ipHeader =
        (headers["x-user-ip"] as string | undefined) ??
        (headers["X-User-Ip"] as string | undefined);

      if (ipHeader) {
        dispatch(setUserIp(ipHeader));
      }
      const availablePayments = Array.isArray(response.data.availablePayments)
        ? (response.data.availablePayments as any[])
        : [];

      return {
        clientSecret: response.data.clientSecret,
        paymentId: response.data.paymentId,
        paymentSession: response.data.paymentSession,
        availablePayments,
        countryCode: regionHeader ? regionHeader.toUpperCase() : undefined,
        currency: currencyHeader || undefined,
      };
    } else {
      return rejectWithValue(
        "Failed to create payment intent due to non-200 response",
      );
    }
  } catch (error: any) {
    if (axiosDefault.isAxiosError(error)) {
      const axiosError = error as AxiosError;
      if (axiosError.response) {
        if (axiosError.response.status === 429) {
          return rejectWithValue("Too many failed requests");
        }
        return rejectWithValue(
          (axiosError.response.data as { message: string }).message ||
            "An unexpected error occurred",
        );
      }
    }
    return rejectWithValue("An unexpected error occurred");
  }
});

export const fetchPaymentProvider = createAsyncThunk<
  PaymentProviderResponse,
  void,
  { rejectValue: string }
>("payment/fetch-provider", async (_, { rejectWithValue }) => {
  try {
    const response = await axiosGetToken.get("/v1/payment/provider");
    if (response.status === 200) {
      return {
        activeProvider: response.data.activeProvider,
        availableProviders: response.data.availableProviders || [],
        backupOrder: response.data.backupOrder || [],
      };
    }
    return rejectWithValue("Failed to load payment provider configuration");
  } catch (error: any) {
    if (axiosDefault.isAxiosError(error) && error.response) {
      return rejectWithValue(
        (error.response.data as { message?: string })?.message ||
          "Failed to load payment provider configuration",
      );
    }
    return rejectWithValue("Failed to load payment provider configuration");
  }
});

export const verifyGameActivation = createAsyncThunk(
  "payment/verify-game-activation",
  async (
    queryParams: {
      paymentIntent?: string;
      clientSecret?: string;
      paymentSession?: string;
      paymentSessionSecret?: string;
      checkoutPaymentId?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const {
        paymentIntent,
        clientSecret,
        paymentSession,
        paymentSessionSecret,
        checkoutPaymentId,
      } = queryParams;

      const params: Record<string, string> = {};
      if (paymentIntent) {
        params.payment_intent = paymentIntent;
      }
      if (clientSecret) {
        params.payment_intent_client_secret = clientSecret;
      }
      if (paymentSession) {
        params.payment_session = paymentSession;
      }
      if (paymentSessionSecret) {
        params.payment_session_secret = paymentSessionSecret;
      }
      if (checkoutPaymentId) {
        params["cko_payment_id"] = checkoutPaymentId;
      }

      const response = await axiosGetToken.get(`/v1/payment/game-activation`, {
        params,
      });

      if (response.status === 200) {
        return response.data.paymentId;
      } else {
        return rejectWithValue("Unauthorized or invalid payment intent.");
      }
    } catch (error: any) {
      if (axiosDefault.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (axiosError.response) {
          const errorMessage =
            (axiosError.response.data as { message: string }).message ||
            "Failed to verify payment.";
          // Check for specific error message indicating payment is still processing
          if (errorMessage.includes("Payment is still processing")) {
            return rejectWithValue("Payment is still processing.");
          }
          return rejectWithValue(errorMessage);
        }
      }
      return rejectWithValue("An unexpected error occurred.");
    }
  },
);
